import { UpdateBorrowerRealEstateCommand } from 'api/borrower';
import { PropertyTypeEnum, RealEstateDto } from 'api/real-estate';
import { GenericLoading } from 'components/ui/Loader';
import { NsKey } from 'enums/NsKey';
import { Form, Formik, FormikHelpers } from 'formik';
import { useDialog } from 'hooks/useDialog';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { borrowersAPI } from 'services/swagger-api';
import {
  BorrowerRealEstateFormValues,
  borrowerRealEstateValidationSchema
} from './borrowerRealEstateForm';
import { BorrowerRealEstateFormLayout } from './BorrowerRealEstateFormLayout';
import { BorrowerRealEstateDetailsSection } from './BorrowerRealEstateDetailsSection';

interface BorrowerRealEstateEditDialogProps {
  borrowerProjectId: string;
  existingBien: RealEstateDto;
  onSuccess: () => void;
}

const formatAddressLabel = (dto: RealEstateDto): string => {
  const parts = [dto.address?.line1, dto.address?.zipCode, dto.address?.city].filter(Boolean);
  return parts.join(' ');
};

const mapDtoToFormValues = (dto: RealEstateDto): BorrowerRealEstateFormValues => ({
  area: dto.area ?? '',
  roomCount: dto.roomCount ?? '',
  propertyType: dto.propertyType ?? '',
  addressSearch: formatAddressLabel(dto),
  line2: dto.address?.line2 ?? '',
  address: {
    address: dto.address?.line1 ?? '',
    postalCode: dto.address?.zipCode ?? '',
    city: dto.address?.city ?? '',
    country: dto.address?.country ?? ''
  }
});

const mapFormToAddress = (values: BorrowerRealEstateFormValues) => ({
  line1: values.address.address,
  line2: values.line2 || undefined,
  zipCode: values.address.postalCode,
  city: values.address.city,
  country: values.address.country
});

export const BorrowerRealEstateEditDialog: FC<BorrowerRealEstateEditDialogProps> = ({
  borrowerProjectId,
  existingBien,
  onSuccess
}) => {
  const { t } = useDynamicTranslation([NsKey.Contractualization, NsKey.RealEstate, NsKey.Common]);
  const { setDialogActions, closeDialog } = useDialog();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues = useMemo(() => mapDtoToFormValues(existingBien), [existingBien]);

  const handleSubmit = useCallback(
    async (
      values: BorrowerRealEstateFormValues,
      helpers: FormikHelpers<BorrowerRealEstateFormValues>
    ) => {
      try {
        setIsSubmitting(true);
        const command: UpdateBorrowerRealEstateCommand = {
          area: values.area !== '' ? Number(values.area) : undefined,
          roomCount: values.roomCount !== '' ? Number(values.roomCount) : undefined,
          propertyType: values.propertyType !== '' ? (values.propertyType as PropertyTypeEnum) : undefined,
          address: mapFormToAddress(values)
        };
        const response = await borrowersAPI.updateBorrowerRealEstate({
          realEstateId: existingBien.id!,
          updateBorrowerRealEstateCommand: command
        });
        if (response.isSuccess) {
          toast.success(t('CONTRACTUALIZATION.REAL_ESTATE.UPDATE_SUCCESS'));
          closeDialog();
          onSuccess();
        } else {
          toast.error(response.errorMessage || t('COMMON.ERROR'));
        }
      } catch (error) {
        console.error(error);
        toast.error(t('COMMON.ERROR'));
      } finally {
        setIsSubmitting(false);
        helpers.setSubmitting(false);
      }
    },
    [existingBien, onSuccess, closeDialog, t]
  );

  return (
    <div className="relative">
      {isSubmitting && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 rounded-lg">
          <GenericLoading loading />
        </div>
      )}
      <Formik<BorrowerRealEstateFormValues>
        enableReinitialize
        validateOnMount
        initialValues={initialValues}
        validationSchema={borrowerRealEstateValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid, submitForm }) => (
          <BorrowerRealEstateEditDialogBody
            isValid={isValid}
            isSubmitting={isSubmitting}
            submitForm={submitForm}
            setDialogActions={setDialogActions}
          />
        )}
      </Formik>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

interface BorrowerRealEstateEditDialogBodyProps {
  isValid: boolean;
  isSubmitting: boolean;
  submitForm: () => Promise<void> | void;
  setDialogActions: ReturnType<typeof useDialog>['setDialogActions'];
}

const BorrowerRealEstateEditDialogBody: FC<BorrowerRealEstateEditDialogBodyProps> = ({
  isValid,
  isSubmitting,
  submitForm,
  setDialogActions
}) => {
  useEffect(() => {
    setDialogActions({
      closeLabel: 'COMMON.CANCEL',
      submitLabel: 'COMMON.VALIDATE',
      submitDisabled: !isValid || isSubmitting,
      onSubmit: () => {
        void submitForm();
      }
    });
    return () => setDialogActions(undefined);
  }, [setDialogActions, isValid, isSubmitting, submitForm]);

  return (
    <Form>
      <div className="flex flex-col gap-4 p-4">
        <BorrowerRealEstateFormLayout />
        <BorrowerRealEstateDetailsSection />
      </div>
    </Form>
  );
};
